import api from './api';

const systemMessageService = {
  // Get all system messages with filters and pagination
  async getMessages(params = {}) {
    try {
      const response = await api.get('/admin/notifications/system-messages/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch system messages');
    }
  },

  // Get a specific system message by ID
  async getMessage(messageId) {
    try {
      const response = await api.get(`/admin/notifications/system-messages/${messageId}/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch system message');
    }
  },

  // Create a new system message
  async createMessage(messageData) {
    try {
      const response = await api.post('/admin/notifications/system-messages/', messageData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create system message');
    }
  },

  // Update an existing system message
  async updateMessage(messageId, messageData) {
    try {
      const response = await api.put(`/admin/notifications/system-messages/${messageId}/`, messageData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to update system message');
    }
  },

  // Delete a system message
  async deleteMessage(messageId) {
    try {
      const response = await api.delete(`/admin/notifications/system-messages/${messageId}/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to delete system message');
    }
  },

  // Send a system message immediately
  async sendMessage(messageId) {
    try {
      const response = await api.post(`/admin/notifications/system-messages/${messageId}/send_now/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send system message');
    }
  },

  // Schedule a system message for later delivery
  async scheduleMessage(messageId) {
    try {
      const response = await api.post(`/admin/notifications/system-messages/${messageId}/schedule/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to schedule system message');
    }
  },

  // Cancel a scheduled message
  async cancelMessage(messageId) {
    try {
      const response = await api.post(`/admin/notifications/system-messages/${messageId}/cancel/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to cancel scheduled message');
    }
  },

  // Get delivery details for a system message
  async getMessageDeliveries(messageId) {
    try {
      const response = await api.get(`/admin/notifications/system-messages/${messageId}/deliveries/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch message deliveries');
    }
  },

  // Get all scheduled messages
  async getScheduledMessages() {
    try {
      const response = await api.get('/admin/notifications/scheduled-messages/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch scheduled messages');
    }
  },

  // Get system message statistics
  async getMessageStats() {
    try {
      const response = await api.get('/admin/notifications/system-messages/stats/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch message statistics');
    }
  },

  // Get message types and their counts
  async getMessageTypeStats() {
    try {
      const response = await api.get('/admin/notifications/system-messages/type-stats/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch message type statistics');
    }
  },

  // Get delivery statistics for a specific message
  async getDeliveryStats(messageId) {
    try {
      const response = await api.get(`/admin/notifications/system-messages/${messageId}/delivery-stats/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch delivery statistics');
    }
  },

  // Bulk operations
  async bulkDeleteMessages(messageIds) {
    try {
      const response = await api.post('/admin/notifications/system-messages/bulk-delete/', {
        message_ids: messageIds
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to bulk delete messages');
    }
  },

  async bulkSendMessages(messageIds) {
    try {
      const response = await api.post('/admin/notifications/system-messages/bulk-send/', {
        message_ids: messageIds
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to bulk send messages');
    }
  },

  async bulkScheduleMessages(messageIds) {
    try {
      const response = await api.post('/admin/notifications/system-messages/bulk-schedule/', {
        message_ids: messageIds
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to bulk schedule messages');
    }
  },

  // Template operations
  async saveAsTemplate(messageId, templateName) {
    try {
      const response = await api.post(`/admin/notifications/system-messages/${messageId}/save-as-template/`, {
        template_name: templateName
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to save message as template');
    }
  },

  async loadFromTemplate(templateId) {
    try {
      const response = await api.get(`/admin/notifications/templates/${templateId}/load/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to load message from template');
    }
  },

  // Preview and test
  async previewMessage(messageData) {
    try {
      const response = await api.post('/admin/notifications/system-messages/preview/', messageData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to preview message');
    }
  },

  async testMessage(messageId, testUsers = []) {
    try {
      const response = await api.post(`/admin/notifications/system-messages/${messageId}/test/`, {
        test_users: testUsers
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to test message');
    }
  }
};

export { systemMessageService };

